import { Team, Player, CreateTeamRequest } from '../types';
import { api } from './api';
import { MOCK_TEAMS, MOCK_PLAYERS, getCurrentMockUserId, hasAccessToPreSeededData, saveMockTeams, saveMockPlayers } from './mockData';

interface CreatePlayerRequest {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  position: string;
  fanNumber?: string;
  nationality?: string;
  photoUrl?: string;
  contactEmail?: string;
  guardians?: { firstName: string; lastName: string; email: string }[];
}

const isDevelopmentMode = process.env.NODE_ENV === 'development';

export const teamService = {
  async getTeams(): Promise<Team[]> {
    try {
      const response = await api.get<{ data: Team[] }>('/teams');
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock team data for development');
        
        // Only show pre-seeded teams to the original mock coach
        if (hasAccessToPreSeededData()) {
          return MOCK_TEAMS;
        }
        
        // For new users, only show teams they've created in this session
        return MOCK_TEAMS.filter(team => team.coachId.startsWith('mock-'));
      }
      throw error;
    }
  },

  async getTeam(teamId: string): Promise<Team> {
    try {
      const response = await api.get<{ data: Team }>(`/teams/${teamId}`);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock team data for development');
        
        const team = MOCK_TEAMS.find((t: Team) => t.id === teamId);
        
        if (!team) {
          throw new Error('Team not found');
        }
        
        // Check if user has access to this team
        if (!hasAccessToPreSeededData() && !team.coachId.startsWith('mock-')) {
          throw new Error('Team not found');
        }
        
        return team;
      }
      throw error;
    }
  },

  async createTeam(data: CreateTeamRequest, logoFile?: File | null): Promise<Team> {
    try {
      // If logo file is provided, convert to base64 and send as JSON
      if (logoFile) {
        const base64Logo = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove data:image/jpeg;base64, prefix
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });

        const requestData = {
          ...data,
          logo: base64Logo,
          logoContentType: logoFile.type,
        };

        const response = await api.post<{ data: Team }>('/teams', requestData);
        return response.data.data;
      }

      // Otherwise, use JSON without logo
      const response = await api.post<{ data: Team }>('/teams', data);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock team creation for development');
        
        // Create mock team (logo upload not supported in mock mode)
        const mockTeam: Team = {
          id: `mock-team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: data.name,
          ageGroup: data.ageGroup,
          season: data.season,
          coachId: getCurrentMockUserId(),
          clubId: '88888888-8888-8888-8888-888888888888',
          assessmentInterval: data.assessmentInterval || 6,
          logoUrl: logoFile ? URL.createObjectURL(logoFile) : undefined,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Add to mock data for this session
        MOCK_TEAMS.push(mockTeam);
        saveMockTeams();
        
        return mockTeam;
      }
      throw error;
    }
  },

  async updateTeam(teamId: string, data: Partial<CreateTeamRequest>, logoFile?: File | null): Promise<Team> {
    try {
      // If logo file is provided, convert to base64 and send as JSON
      if (logoFile) {
        const base64Logo = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove data:image/jpeg;base64, prefix
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });

        const requestData = {
          ...data,
          logo: base64Logo,
          logoContentType: logoFile.type,
        };

        const response = await api.put<{ data: Team }>(`/teams/${teamId}`, requestData);
        return response.data.data;
      }

      // Otherwise, use JSON without logo
      const response = await api.put<{ data: Team }>(`/teams/${teamId}`, data);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock team update for development');
        
        // Find the team in mock data
        const teamIndex = MOCK_TEAMS.findIndex((team: Team) => team.id === teamId);
        if (teamIndex === -1) {
          throw new Error('Team not found');
        }
        
        const team = MOCK_TEAMS[teamIndex];
        
        // Check if user has access to update this team
        if (!hasAccessToPreSeededData() && !team.coachId.startsWith('mock-')) {
          throw new Error('Access denied');
        }
        
        // Update the team
        const updatedTeam = {
          ...team,
          ...data,
          logoUrl: logoFile ? URL.createObjectURL(logoFile) : team.logoUrl,
          updatedAt: new Date()
        };
        
        MOCK_TEAMS[teamIndex] = updatedTeam;
        saveMockTeams();
        return updatedTeam;
      }
      throw error;
    }
  },

  async deleteTeam(teamId: string): Promise<void> {
    try {
      await api.delete(`/teams/${teamId}`);
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock team deletion for development');
        
        // Find and remove the team from mock data
        const teamIndex = MOCK_TEAMS.findIndex((team: Team) => team.id === teamId);
        if (teamIndex === -1) {
          throw new Error('Team not found');
        }
        
        // Check if user has access to delete this team
        const team = MOCK_TEAMS[teamIndex];
        if (!hasAccessToPreSeededData() && !team.coachId.startsWith('mock-')) {
          throw new Error('Access denied');
        }
        
        // Remove team from mock data
        MOCK_TEAMS.splice(teamIndex, 1);
        saveMockTeams();
        
        // Also remove associated players
        for (let i = MOCK_PLAYERS.length - 1; i >= 0; i--) {
          if (MOCK_PLAYERS[i].teamId === teamId) {
            MOCK_PLAYERS.splice(i, 1);
          }
        }
        saveMockPlayers();
        
        return;
      }
      throw error;
    }
  },

  async getTeamPlayers(teamId: string): Promise<Player[]> {
    try {
      const response = await api.get<{ data: Player[] }>(`/teams/${teamId}/players`);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock player data for development');
        
        // Filter players based on team access
        const teamPlayers = MOCK_PLAYERS.filter((player: Player) => player.teamId === teamId);
        console.log('All MOCK_PLAYERS:', MOCK_PLAYERS);
        console.log('Filtered teamPlayers for teamId', teamId, ':', teamPlayers);
        
        // If not the original mock coach, only show players from teams they have access to
        const hasPreSeededAccess = hasAccessToPreSeededData();
        console.log('hasAccessToPreSeededData:', hasPreSeededAccess);
        
        if (!hasPreSeededAccess) {
          const team = MOCK_TEAMS.find((t: Team) => t.id === teamId);
          console.log('Found team:', team);
          
          if (team && !team.coachId.startsWith('mock-')) {
            console.log('Blocking access to pre-seeded team players');
            return []; // No access to pre-seeded team players
          }
        }
        
        console.log('Returning teamPlayers:', teamPlayers);
        return teamPlayers;
      }
      throw error;
    }
  },

  async createPlayer(teamId: string, data: CreatePlayerRequest): Promise<Player> {
    try {
      const response = await api.post<{ data: Player }>(`/teams/${teamId}/players`, data);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock player creation for development');
        
        // Create mock player
        const mockPlayer: Player = {
          id: `mock-player-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          firstName: data.firstName,
          lastName: data.lastName,
          dateOfBirth: new Date(data.dateOfBirth),
          position: data.position,
          fanNumber: data.fanNumber,
          nationality: data.nationality,
          teamId: teamId,
          parentIds: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Add to mock data for this session
        MOCK_PLAYERS.push(mockPlayer);
        saveMockPlayers();
        
        return mockPlayer;
      }
      throw error;
    }
  },

  async updatePlayer(playerId: string, data: Partial<CreatePlayerRequest>): Promise<Player> {
    try {
      const response = await api.put<{ data: Player }>(`/players/${playerId}`, data);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock player update for development');
        
        // Find the player in mock data
        const playerIndex = MOCK_PLAYERS.findIndex((player: Player) => player.id === playerId);
        if (playerIndex === -1) {
          throw new Error('Player not found');
        }
        
        const player = MOCK_PLAYERS[playerIndex];
        
        // Check if user has access to update this player (via team ownership)
        const team = MOCK_TEAMS.find((t: Team) => t.id === player.teamId);
        if (team && !hasAccessToPreSeededData() && !team.coachId.startsWith('mock-')) {
          throw new Error('Access denied');
        }
        
        // Update the player
        const updatedPlayer = {
          ...player,
          ...data,
          dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : player.dateOfBirth,
          updatedAt: new Date()
        };
        
        MOCK_PLAYERS[playerIndex] = updatedPlayer;
        saveMockPlayers();
        return updatedPlayer;
      }
      throw error;
    }
  },

  async deletePlayer(playerId: string): Promise<void> {
    try {
      await api.delete(`/players/${playerId}`);
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock player deletion for development');
        
        // Find the player in mock data
        const playerIndex = MOCK_PLAYERS.findIndex((player: Player) => player.id === playerId);
        if (playerIndex === -1) {
          throw new Error('Player not found');
        }
        
        const player = MOCK_PLAYERS[playerIndex];
        
        // Check if user has access to delete this player (via team ownership)
        const team = MOCK_TEAMS.find((t: Team) => t.id === player.teamId);
        if (team && !hasAccessToPreSeededData() && !team.coachId.startsWith('mock-')) {
          throw new Error('Access denied');
        }
        
        // Remove player from mock data
        MOCK_PLAYERS.splice(playerIndex, 1);
        saveMockPlayers();
        
        return;
      }
      throw error;
    }
  },

  async getPlayer(playerId: string): Promise<Player> {
    try {
      const response = await api.get<{ data: Player }>(`/players/${playerId}`);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock player data for development');
        
        const player = MOCK_PLAYERS.find((p: Player) => p.id === playerId);
        
        if (!player) {
          throw new Error('Player not found');
        }
        
        // Check if user has access to this player (via team ownership)
        const team = MOCK_TEAMS.find((t: Team) => t.id === player.teamId);
        if (team && !hasAccessToPreSeededData() && !team.coachId.startsWith('mock-')) {
          throw new Error('Player not found');
        }
        
        return player;
      }
      throw error;
    }
  },

  async removeCollaborator(teamId: string, coachId: string): Promise<void> {
    await api.delete(`/teams/${teamId}/coaches/${coachId}`);
  },

  async importPlayers(teamId: string, csvData: string): Promise<Player[]> {
    try {
      const response = await api.post<{ data: Player[] }>(`/teams/${teamId}/players/import`, {
        csvData,
      });
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock CSV import for development');
        
        // Parse CSV data
        const lines = csvData.trim().split('\n');
        if (lines.length < 2) {
          throw new Error('CSV must contain header row and at least one data row');
        }
        
        const headers = lines[0].split(',').map(h => h.trim());
        const expectedHeaders = ['firstName', 'lastName', 'dateOfBirth', 'position', 'fanNumber'];
        
        // Validate headers
        if (!expectedHeaders.every(header => headers.includes(header))) {
          throw new Error(`CSV must contain these headers: ${expectedHeaders.join(', ')}`);
        }
        
        const importedPlayers: Player[] = [];
        
        // Process each data row
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length !== headers.length) {
            throw new Error(`Row ${i + 1}: Expected ${headers.length} columns, got ${values.length}`);
          }
          
          const playerData: any = {};
          headers.forEach((header, index) => {
            playerData[header] = values[index] || undefined;
          });
          
          // Validate required fields
          if (!playerData.firstName || !playerData.lastName || !playerData.dateOfBirth || !playerData.position) {
            throw new Error(`Row ${i + 1}: Missing required fields (firstName, lastName, dateOfBirth, position)`);
          }
          
          // Validate position
          const validPositions = ['Goalkeeper', 'Defender', 'Midfield', 'Winger', 'Forward'];
          if (!validPositions.includes(playerData.position)) {
            throw new Error(`Row ${i + 1}: Invalid position "${playerData.position}". Must be one of: ${validPositions.join(', ')}`);
          }
          
          // Validate date format
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(playerData.dateOfBirth)) {
            throw new Error(`Row ${i + 1}: Invalid date format "${playerData.dateOfBirth}". Use YYYY-MM-DD format`);
          }
          
          // Create mock player
          const mockPlayer: Player = {
            id: `mock-player-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            firstName: playerData.firstName,
            lastName: playerData.lastName,
            dateOfBirth: new Date(playerData.dateOfBirth),
            position: playerData.position,
            fanNumber: playerData.fanNumber || undefined,
            teamId: teamId,
            parentIds: [],
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          // Add to mock data for this session
          MOCK_PLAYERS.push(mockPlayer);
          importedPlayers.push(mockPlayer);
        }
        
        saveMockPlayers();
        return importedPlayers;
      }
      throw error;
    }
  },
};